import { useMemo, useState } from "react";
import { Link } from "@/lib/router-compat";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, MapPin, Search } from "lucide-react";
import { counties, municipalities, topMunicipalities } from "@/data/municipalities";

interface Props {
  /** Basväg som kommun-slug läggs till på, t.ex. "/elpriser" */
  basePath: string;
  heading?: string;
  description?: string;
  /** Visa endast de 10 största kommunerna tills användaren expanderar. Alla 290 länkar finns kvar i HTML för sökmotorer. */
  collapsible?: boolean;
}

/**
 * Sökbar lista med Sveriges alla 290 kommuner, grupperad per län.
 * I minimerad vy visas de 10 största kommunerna, men den fullständiga listan
 * renderas alltid i DOM:en (dold med CSS) så att alla kommunsidor förblir
 * länkade och indexerbara – inga orphan pages.
 */
const MunicipalityIndex = ({ basePath, heading, description, collapsible = true }: Props) => {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);

  const searching = query.trim().length > 0;
  const showAll = !collapsible || expanded || searching;

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? municipalities.filter(
          (m) =>
            m.displayName.toLowerCase().includes(q) ||
            m.name.toLowerCase().includes(q) ||
            m.county.toLowerCase().includes(q),
        )
      : municipalities;
    return counties
      .map((county) => ({ county, items: filtered.filter((m) => m.county === county) }))
      .filter((g) => g.items.length > 0);
  }, [query]);

  const linkClass =
    "text-sm px-3 py-2 rounded-md border hover:border-primary hover:text-primary transition-colors";

  return (
    <div>
      {heading && <h2 className="heading-2 mb-2">{heading}</h2>}
      {description && <p className="text-muted-foreground mb-6 text-sm">{description}</p>}

      <div className="relative max-w-md mb-8">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          size={16}
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Sök kommun eller län"
          className="pl-9"
          aria-label="Sök kommun"
        />
      </div>

      {/* Minimerad vy: de 10 största kommunerna */}
      {collapsible && !showAll && (
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-muted-foreground uppercase mb-3 flex items-center gap-2">
            <MapPin size={14} /> Sveriges 10 största kommuner
          </h3>
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {topMunicipalities.map((m) => (
              <Link key={m.slug} to={`${basePath}/${m.slug}`} className={linkClass}>
                {m.displayName}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Fullständig lista – alltid i DOM:en för sökmotorer, dold i minimerad vy */}
      <div className={showAll ? "" : "hidden"} aria-hidden={showAll ? undefined : true}>
        {grouped.length === 0 ? (
          <p className="text-muted-foreground">Ingen kommun matchar sökningen.</p>
        ) : (
          <div className="space-y-8">
            {grouped.map((g) => (
              <div key={g.county}>
                <h3 className="text-lg md:text-xl font-semibold text-muted-foreground uppercase mb-3 flex items-center gap-2">
                  <MapPin size={14} /> {g.county} län
                </h3>
                <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                  {g.items.map((m) => (
                    <Link key={m.slug} to={`${basePath}/${m.slug}`} className={linkClass}>
                      {m.displayName}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {collapsible && !searching && (
        <Button
          type="button"
          variant="outline"
          className="mt-6"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              <ChevronUp size={16} /> Visa färre kommuner
            </>
          ) : (
            <>
              <ChevronDown size={16} /> Visa alla {municipalities.length} kommuner
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default MunicipalityIndex;
